import { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, Stack, Chip, Avatar, Skeleton } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { PRIMARY, ACCENT, NAV_BG } from '../theme';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

const CARD_HEIGHT = 148;

const PALETTES = {
  primary: { from: '#0B224F', to: '#1B3A78', tint: 'rgba(11,34,79,0.08)' },
  success: { from: '#0F766E', to: '#14B8A6', tint: 'rgba(20,184,166,0.12)' },
  warning: { from: '#B45309', to: '#F59E0B', tint: 'rgba(245,158,11,0.14)' },
  info:    { from: '#1D4ED8', to: '#3B82F6', tint: 'rgba(59,130,246,0.12)' }
};

function StatCard({ label, value, sub, icon, tone = 'primary', trend }) {
  const p = PALETTES[tone] || PALETTES.primary;
  return (
    <Card
      elevation={0}
      sx={{
        height: CARD_HEIGHT,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(11,34,79,0.08)',
        borderRadius: 3,
        background: '#ffffff',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.10)',
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 8px rgba(15,23,42,0.06), 0 18px 40px -16px rgba(15,23,42,0.20)',
          borderColor: 'rgba(11,34,79,0.18)'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${p.from}, ${p.to})`
        }
      }}
    >
      <CardContent sx={{ height: '100%', p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ height: '100%' }}>
          <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                fontSize: 11
              }}
              noWrap
            >
              {label}
            </Typography>
            <Typography
              sx={{
                color: NAV_BG,
                fontWeight: 700,
                fontSize: 28,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                wordBreak: 'break-word'
              }}
            >
              {value}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto' }}>
              {sub && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }} noWrap>
                  {sub}
                </Typography>
              )}
              {trend && (
                <Chip
                  size="small"
                  icon={<TrendingUpRoundedIcon sx={{ fontSize: 14 }} />}
                  label={trend}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: p.tint,
                    color: p.from,
                    '& .MuiChip-icon': { color: p.from, ml: '4px' }
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Avatar
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${p.from}, ${p.to})`,
              boxShadow: `0 8px 20px -8px ${p.from}`,
              flexShrink: 0
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

const panelSx = {
  borderRadius: 3,
  border: '1px solid rgba(11,34,79,0.08)',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.10)'
};

export default function DashboardPage() {
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get('/api/dashboard').then(r => setS(r.data)); }, []);

  if (!s) {
    return (
      <Box>
        <Typography variant="h4" mb={3}>Dashboard</Typography>
        <Grid container spacing={2}>
          {[0,1,2,3].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={CARD_HEIGHT} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const fmt = (n) =>
    `${s.currency || ''} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Overview of tenants, revenue and support activity.</Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            tone="primary"
            label="Tenants"
            value={s.totalTenants}
            sub={`${s.activeTenants} active`}
            icon={<GroupsRoundedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            tone="success"
            label="Monthly Recurring Revenue"
            value={fmt(s.mrr)}
            icon={<PaidRoundedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            tone="warning"
            label="Open Tickets"
            value={s.openTickets}
            sub={`${s.criticalTickets} critical`}
            icon={<SupportAgentRoundedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            tone="info"
            label="Resolved this month"
            value={s.resolvedThisMonth}
            icon={<TaskAltRoundedIcon />}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={panelSx}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Revenue trend</Typography>
                <Chip size="small" label="Last 12 months" sx={{ bgcolor: 'rgba(11,34,79,0.06)', color: PRIMARY, fontWeight: 600 }} />
              </Stack>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={s.revenueTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rpRevStroke" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={PRIMARY} />
                        <stop offset="100%" stopColor={ACCENT} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={{ stroke: 'rgba(15,23,42,0.12)' }} />
                    <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={{ stroke: 'rgba(15,23,42,0.12)' }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid rgba(15,23,42,0.08)',
                        boxShadow: '0 10px 30px -10px rgba(15,23,42,0.25)'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="url(#rpRevStroke)"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: PRIMARY }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={panelSx}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Recent tickets</Typography>
              <Stack spacing={0.5} divider={<Box sx={{ borderBottom: '1px solid rgba(15,23,42,0.06)' }} />}>
                {(s.recentTickets || []).map(t => (
                  <Box
                    key={t.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'background-color .15s ease',
                      '&:hover': { bgcolor: 'rgba(11,34,79,0.04)' }
                    }}
                    onClick={() => nav(`/tickets/${t.id}`)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>{t.ticketNumber}</Typography>
                      <Chip size="small" label={t.status} sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>{t.title}</Typography>
                  </Box>
                ))}
                {(s.recentTickets || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">No tickets yet</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

